import React, { useEffect, useState, useRef, createContext, createElement } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HeroSection } from './components/HeroSection';
import { TutorialVideoSection } from './components/TutorialVideoSection';
import { HowItWorksSection } from './components/HowItWorksSection';
import { BenefitsSection } from './components/BenefitsSection';
import { PricingSection } from './components/PricingSection';
import { FoundersNoteSection } from './components/FoundersNoteSection';
import { FAQSection } from './components/FAQSection';
import { TestimonialsSection } from './components/TestimonialsSection';
import { MoreThanLessonsSection } from './components/MoreThanLessonsSection';
import { VisionSection } from './components/VisionSection';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { TeacherSignUp } from './components/pages/TeacherSignUp';
import { StudentSignUp } from './components/pages/StudentSignUp';
import { StudentLogin } from './components/pages/StudentLogin';
import { TeacherLogin } from './components/pages/TeacherLogin';
import { StudentDashboard } from './components/pages/StudentDashboard';
import { StudentMessages } from './components/pages/StudentMessages';
import { TeacherMessages } from './components/pages/TeacherMessages';
import { FindTeachers } from './components/pages/FindTeachers';
import { CompleteTeacherProfile } from './components/pages/CompleteTeacherProfile';
import { TeacherPublicProfile } from './components/pages/TeacherPublicProfile';
import { TeacherDashboard } from './components/pages/TeacherDashboard';
import { TeacherProfile } from './components/pages/TeacherProfile';
import { ReportsDashboard } from './components/pages/ReportsDashboard';
import { OnlineCourses } from './components/pages/OnlineCourses';
import { PublicCoursesPage } from './components/pages/PublicCoursesPage';
import { MyStudents } from './components/pages/MyStudents';
import { StudentSettings } from './components/pages/StudentSettings';
import { StudentSchedule } from './components/pages/StudentSchedule';
import { StudentProfilePage } from './components/pages/StudentProfilePage';
import { StudentDataCheck } from './components/pages/StudentDataCheck';
import { AuthProvider } from './contexts/AuthContext';
import { AuthGuard } from './components/AuthGuard';
import { SubscriptionGuard } from './components/SubscriptionGuard';
import { ScrollToTop } from './components/ScrollToTop';
import { SignInModal } from './components/SignInModal';
import { TeacherLoginModal } from './components/TeacherLoginModal';
import { initEmailService } from './services/emailService';
import { EmailVerificationPage } from './components/pages/EmailVerificationPage';
import { InstrumentSelectionPage } from './components/pages/InstrumentSelectionPage';
import { TeacherMatchingPage } from './components/pages/TeacherMatchingPage';
import { SheetMusicPage } from './components/pages/SheetMusicPage';
import { StudentMyProfile } from './components/pages/StudentMyProfile';
import { StudentAccountSettings } from './components/pages/StudentAccountSettings';
import { MoonlightSonataViewer } from './components/pages/MoonlightSonataViewer';
import { PreludeInCMajorViewer } from './components/pages/PreludeInCMajorViewer';
import { ToccatinaViewer } from './components/pages/ToccatinaViewer';
import { RondoInEFlatViewer } from './components/pages/RondoInEFlatViewer';
import { PolonaiseInFViewer } from './components/pages/PolonaiseInFViewer';
import { EnglishSuite1Bourree1Viewer } from './components/pages/EnglishSuite1Bourree1Viewer';
import { EnglishSuite1Bourree2Viewer } from './components/pages/EnglishSuite1Bourree2Viewer';
import { EnglishSuite2Bourree1Viewer } from './components/pages/EnglishSuite2Bourree1Viewer';
import { EnglishSuite2Bourree2Viewer } from './components/pages/EnglishSuite2Bourree2Viewer';
import { WTK1Prelude1Viewer } from './components/pages/WTK1Prelude1Viewer';
import { WTK1Fugue1Viewer } from './components/pages/WTK1Fugue1Viewer';
import { WTK1Prelude2Viewer } from './components/pages/WTK1Prelude2Viewer';
import { AusMeinesHerzensGrundeViewer } from './components/pages/AusMeinesHerzensGrundeViewer';
import { IchDankDirLieberHerreViewer } from './components/pages/IchDankDirLieberHerreViewer';
import { ErmuntereDichMeinSchwacherGeistViewer } from './components/pages/ErmuntereDichMeinSchwacherGeistViewer';
import { GottWieGrossIstDeineGuteViewer } from './components/pages/GottWieGrossIstDeineGuteViewer';
import { GibDichZufriedenUndSeiStilleViewer } from './components/pages/GibDichZufriedenUndSeiStilleViewer';
import { GibDichZufriedenUndSeiStilleDMinorViewer } from './components/pages/GibDichZufriedenUndSeiStilleDMinorViewer';
import { AriaBWV515Viewer } from './components/pages/AriaBWV515Viewer';
import { WarumBetrubstDuDichViewer } from './components/pages/WarumBetrubstDuDichViewer';
import { InventionNo2Viewer } from './components/pages/InventionNo2Viewer';
import { InventionNo3Viewer } from './components/pages/InventionNo3Viewer';
import { InventionNo4Viewer } from './components/pages/InventionNo4Viewer';
import { InventionNo5Viewer } from './components/pages/InventionNo5Viewer';
import { InventionNo7Viewer } from './components/pages/InventionNo7Viewer';
import { InventionNo8Viewer } from './components/pages/InventionNo8Viewer';
import { InventionNo10Viewer } from './components/pages/InventionNo10Viewer';
import { InventionNo11Viewer } from './components/pages/InventionNo11Viewer';
import { InventionNo13Viewer } from './components/pages/InventionNo13Viewer';
import { InventionNo14Viewer } from './components/pages/InventionNo14Viewer';
import { InventionNo15Viewer } from './components/pages/InventionNo15Viewer';
import { GenericSheetMusicViewer } from './components/pages/GenericSheetMusicViewer';
import { TeacherTasks } from './components/pages/TeacherTasks';
import { StudentLayout } from './components/layouts/StudentLayout';
import { TeacherLayout } from './components/layouts/TeacherLayout';
import { RoleAwareLayout } from './components/layouts/RoleAwareLayout';
import { VirtualClassroom } from './components/pages/VirtualClassroom';
import { ToastContainer } from './components/ToastContainer';
import { PaymentMethodsPage } from './components/pages/PaymentMethodsPage';
import { TransactionHistoryPage } from './components/pages/TransactionHistoryPage';
import { SubscriptionManagementPage } from './components/pages/SubscriptionManagementPage';
import { Calendar2 } from './components/pages/Calendar2';
import { MyInfoPage } from './components/pages/MyInfoPage';
import { SettingsPage } from './components/pages/SettingsPage';
import { AccountSecurityPage } from './components/pages/AccountSecurityPage';
import { BillingPayoutsPage } from './components/pages/BillingPayoutsPage';
import { HelpSupportPage } from './components/pages/HelpSupportPage';
import { PayoutsInvoicesPage } from './components/pages/PayoutsInvoicesPage';
import { ThemeProvider, useTheme } from './components/providers/ThemeProvider';
import { ForgotPassword } from './components/pages/ForgotPassword';
import { ResetPasswordPage } from './components/pages/ResetPasswordPage';
import { TeacherEmailVerificationPage } from './components/pages/TeacherEmailVerificationPage';
import { VerifyEmailPage } from './components/pages/VerifyEmailPage';
import { EmailVerifiedPage } from './components/pages/EmailVerifiedPage';
import { Toaster } from 'react-hot-toast';
import { RevenuePage } from './components/pages/RevenuePage';
import { InvoicePage } from './components/pages/InvoicePage';
import { TeacherPublicProfilePage } from './components/pages/TeacherPublicProfilePage';
import { AboutUsPage } from './components/pages/AboutUsPage';
import { CareersPage } from './components/pages/CareersPage';
import { BlogListPage } from './components/pages/BlogListPage';
import { BlogArticlePage } from './components/pages/BlogArticlePage';
import { KnowledgeBaseGettingStarted } from './components/pages/KnowledgeBaseGettingStarted';
import { KnowledgeBaseSchedulingLessons } from './components/pages/KnowledgeBaseSchedulingLessons';
import { KnowledgeBaseVirtualClassroom } from './components/pages/KnowledgeBaseVirtualClassroom';
import { PrivacyPolicyPage } from './components/pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './components/pages/CookiePolicyPage';
import { TermsOfServicePage } from './components/pages/TermsOfServicePage';
import { RefundPolicyPage } from './components/pages/RefundPolicyPage';
import { HowItWorksPage } from './components/pages/HowItWorksPage';
import { BenefitsPage } from './components/pages/BenefitsPage';
import { PricingPage } from './components/pages/PricingPage';
import { TutorialPage } from './components/pages/TutorialPage';
import { ContactPage } from './components/pages/ContactPage';
import { LoginPage } from './components/pages/LoginPage';
import { SitemapXml } from './components/pages/SitemapXml';
import { StudentDevGuard } from './components/StudentDevGuard';
import { StudentAuthGuard } from './components/StudentAuthGuard';
// Note: SignInModalContext and TeacherLoginModalContext are defined locally below via createContext.
// Do NOT re-import them from SignInModal or TeacherLoginModal (circular dependency breaks Safari).
// Create a context for the sign in modal
export const SignInModalContext = createContext({
  isSignInModalOpen: false,
  openSignInModal: () => {},
  closeSignInModal: () => {},
  isTeacherLoginModalOpen: false,
  openTeacherLoginModal: () => {},
  closeTeacherLoginModal: () => {}
});
// Create a wrapper component that uses the theme
function AppContent() {
  const {
    theme
  } = useTheme();
  // Initialize EmailJS when the app loads
  useEffect(() => {
    initEmailService();
  }, []);
  // Set the correct page title (overrides any template/sandbox title)
  useEffect(() => {
    document.title = 'Maestro Connect | Professional Tools for Music Teachers';
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Manage lessons, students, scheduling, and growth — all in one place. Built specifically for private music educators.';
    // Set Open Graph tags
    const ogTags: Record<string, string> = {
      'og:title': 'Maestro Connect | Professional Tools for Music Teachers',
      'og:description': 'Manage lessons, students, scheduling, and growth — all in one place. Built specifically for private music educators.',
      'og:type': 'website',
      'og:url': 'https://maestroconnect.io/',
      'og:image': 'https://uploadthingy.s3.us-west-1.amazonaws.com/1YYDABxxgC3iLBdkcPAnB8/Pianist.jpg',
      'og:image:width': '1200',
      'og:image:height': '630'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  // Global state for sign in modal
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isTeacherLoginModalOpen, setIsTeacherLoginModalOpen] = useState(false);
  // EMAIL VERIFICATION FIX: Track if modal was opened via verification redirect
  // This ref prevents auth state transitions from closing the modal
  const modalOpenedByVerification = useRef(false);
  const openSignInModal = () => {
    setIsSignInModalOpen(true);
  };
  const closeSignInModal = () => {
    setIsSignInModalOpen(false);
  };
  const openTeacherLoginModal = () => {
    setIsTeacherLoginModalOpen(true);
  };
  const closeTeacherLoginModal = () => {
    modalOpenedByVerification.current = false;
    setIsTeacherLoginModalOpen(false);
  };
  // EMAIL VERIFICATION FIX: Auto-open TeacherLoginModal when redirected from email verification
  // When EmailVerifiedPage redirects unauthenticated users to /?openLogin=true&verified=true,
  // this effect detects the param and opens the login modal automatically.
  // ROLE-AWARE: If the user is a student, redirect to student login instead.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('openLogin') === 'true') {
      const storedUserType = localStorage.getItem('userType');
      if (storedUserType === 'student') {
        // Students should never see the teacher login modal — redirect to student login
        console.log('[App] Detected openLogin param for student — redirecting to student login');
        const isVerified = params.get('verified') === 'true';
        const studentLoginUrl = isVerified ? '/student-login?verified=true' : '/student-login';
        // Clean up and redirect
        window.location.replace(studentLoginUrl);
        return;
      }
      console.log('[App] Detected openLogin param — auto-opening Teacher Login modal');
      modalOpenedByVerification.current = true;
      // Small delay to ensure the app is fully mounted before opening modal
      const timer = setTimeout(() => {
        setIsTeacherLoginModalOpen(true);
        // Clean up URL params without triggering navigation
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);
  return <AuthProvider data-id="element-0">
      <SignInModalContext.Provider value={{
      isSignInModalOpen,
      openSignInModal,
      closeSignInModal,
      isTeacherLoginModalOpen,
      openTeacherLoginModal,
      closeTeacherLoginModal
    }} data-id="element-1">
        <div className="w-full min-h-dvh bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-2">
          <BrowserRouter data-id="element-3">
            <ScrollToTop data-id="element-4" />
            <Routes data-id="element-5">
              <Route path="/" element={<>
                    <Navbar data-id="element-7" />
                    <main data-id="element-8">
                      <HeroSection data-id="element-9" />
                      <TutorialVideoSection data-id="element-10" />
                      <HowItWorksSection data-id="element-11" />
                      <BenefitsSection data-id="element-12" />
                      <MoreThanLessonsSection data-id="element-13" />
                      <TestimonialsSection data-id="element-14" />
                      <FoundersNoteSection data-id="element-15" />
                      <VisionSection data-id="element-16" />
                      <PricingSection data-id="element-17" />
                      <FAQSection data-id="element-18" />
                    </main>
                    <Footer data-id="element-19" />
                  </>} data-id="element-6" />
              <Route path="/sitemap.xml" element={<SitemapXml data-id="element-21" />} data-id="element-20" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-23" />} data-id="element-22" />
              <Route path="/benefits" element={<BenefitsPage data-id="element-25" />} data-id="element-24" />
              <Route path="/pricing" element={<PricingPage data-id="element-27" />} data-id="element-26" />
              <Route path="/tutorial" element={<TutorialPage data-id="element-29" />} data-id="element-28" />
              <Route path="/contact" element={<ContactPage data-id="element-31" />} data-id="element-30" />
              <Route path="/login" element={<LoginPage data-id="element-33" />} data-id="element-32" />
              <Route path="/teacher-signup" element={<TeacherSignUp data-id="element-35" />} data-id="element-34" />
              <Route path="/student-signup" element={<StudentDevGuard data-id="element-37">
                    <StudentSignUp data-id="element-38" />
                  </StudentDevGuard>} data-id="element-36" />
              <Route path="/forgot-password" element={<ForgotPassword data-id="element-40" />} data-id="element-39" />
              <Route path="/reset-password" element={<ResetPasswordPage data-id="element-42" />} data-id="element-41" />
              <Route path="/student-login" element={<StudentDevGuard data-id="element-44">
                    <StudentLogin data-id="element-45" />
                  </StudentDevGuard>} data-id="element-43" />
              <Route path="/teacher-login" element={<TeacherLogin data-id="element-47" />} data-id="element-46" />

              {/* Email verification routes - NO AUTH GUARD (must be accessible to unverified users) */}
              <Route path="/email-verification" element={<EmailVerificationPage data-id="element-49" />} data-id="element-48" />
              <Route path="/teacher-email-verification" element={<TeacherEmailVerificationPage data-id="element-51" />} data-id="element-50" />
              {/* New route for email verification success */}
              <Route path="/email-verified" element={<EmailVerifiedPage data-id="element-53" />} data-id="element-52" />
              {/* Legacy route for handling email verification links - kept for backward compatibility */}
              <Route path="/verify-email" element={<VerifyEmailPage data-id="element-55" />} data-id="element-54" />

              {/* Student routes with consistent sidebar - GATED by StudentDevGuard + StudentAuthGuard */}
              <Route path="/student-dashboard" element={<StudentDevGuard data-id="element-57">
                    <StudentAuthGuard data-id="element-58">
                      <StudentLayout data-id="element-59">
                        <StudentDashboard data-id="element-60" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-56" />
              <Route path="/messages" element={<StudentDevGuard data-id="element-62">
                    <StudentAuthGuard data-id="element-63">
                      <StudentLayout data-id="element-64">
                        <StudentMessages data-id="element-65" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-61" />
              <Route path="/find-teachers" element={<StudentDevGuard data-id="element-67">
                    <StudentAuthGuard data-id="element-68">
                      <StudentLayout data-id="element-69">
                        <FindTeachers data-id="element-70" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-66" />
              <Route path="/online-courses" element={<StudentDevGuard data-id="element-72">
                    <StudentAuthGuard data-id="element-73">
                      <StudentLayout data-id="element-74">
                        <OnlineCourses data-id="element-75" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-71" />
              <Route path="/student-schedule" element={<StudentDevGuard data-id="element-77">
                    <StudentAuthGuard data-id="element-78">
                      <StudentLayout data-id="element-79">
                        <StudentSchedule data-id="element-80" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-76" />
              <Route path="/sheet-music" element={<StudentDevGuard data-id="element-82">
                    <StudentAuthGuard data-id="element-83">
                      <StudentLayout data-id="element-84">
                        <SheetMusicPage data-id="element-85" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-81" />
              <Route path="/student-settings" element={<StudentDevGuard data-id="element-87">
                    <StudentAuthGuard data-id="element-88">
                      <StudentLayout data-id="element-89">
                        <StudentSettings data-id="element-90" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-86" />
              {/* PHASE 2: Student self-profile route */}
              <Route path="/student/profile" element={<StudentDevGuard data-id="element-92">
                    <StudentAuthGuard data-id="element-93">
                      <StudentLayout data-id="element-94">
                        <StudentMyProfile data-id="element-95" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-91" />
              <Route path="/my-profile" element={<StudentDevGuard data-id="element-97">
                    <StudentAuthGuard data-id="element-98">
                      <StudentLayout data-id="element-99">
                        <StudentMyProfile data-id="element-100" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-96" />
              <Route path="/account-settings" element={<StudentDevGuard data-id="element-102">
                    <StudentAuthGuard data-id="element-103">
                      <StudentLayout data-id="element-104">
                        <StudentAccountSettings data-id="element-105" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-101" />

              {/* Teacher routes - PROTECTED with email verification enforcement */}
              <Route path="/teacher-dashboard" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-107">
                    <SubscriptionGuard data-id="element-108">
                      <TeacherLayout data-id="element-109">
                        <TeacherDashboard data-id="element-110" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-106" />
              <Route path="/teacher-profile" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-112">
                    <SubscriptionGuard data-id="element-113">
                      <TeacherPublicProfilePage data-id="element-114" />
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-111" />

              {/* Teacher calendar routes - PROTECTED */}
              <Route path="/teacher/calendar" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-116">
                    <SubscriptionGuard data-id="element-117">
                      <TeacherLayout data-id="element-118">
                        <Calendar2 data-id="element-119" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-115" />
              <Route path="/teacher/calendar2" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-121">
                    <SubscriptionGuard data-id="element-122">
                      <TeacherLayout data-id="element-123">
                        <Calendar2 data-id="element-124" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-120" />

              {/* Public teacher profile routes - NO AUTH GUARD (public pages) */}
              <Route path="/teachers/:teacherId" element={<TeacherPublicProfilePage data-id="element-126" />} data-id="element-125" />

              {/* Teacher-only routes - PROTECTED */}
              <Route path="/invoices" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-128">
                    <SubscriptionGuard data-id="element-129">
                      <TeacherLayout data-id="element-130">
                        <InvoicePage data-id="element-131" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-127" />
              <Route path="/revenue" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-133">
                    <SubscriptionGuard data-id="element-134">
                      <TeacherLayout data-id="element-135">
                        <RevenuePage data-id="element-136" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-132" />
              <Route path="/assigned-tasks" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-138">
                    <SubscriptionGuard data-id="element-139">
                      <TeacherLayout data-id="element-140">
                        <TeacherTasks data-id="element-141" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-137" />
              <Route path="/reports" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-143">
                    <SubscriptionGuard data-id="element-144">
                      <TeacherLayout data-id="element-145">
                        <ReportsDashboard data-id="element-146" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-142" />
              <Route path="/payouts" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-148">
                    <SubscriptionGuard data-id="element-149">
                      <PayoutsInvoicesPage data-id="element-150" />
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-147" />
              <Route path="/complete-profile" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-152">
                    <CompleteTeacherProfile data-id="element-153" />
                  </AuthGuard>} data-id="element-151" />
              <Route path="/my-students" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-155">
                    <SubscriptionGuard data-id="element-156">
                      <TeacherLayout data-id="element-157">
                        <MyStudents data-id="element-158" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-154" />
              <Route path="/student-data-check" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-160">
                    <SubscriptionGuard data-id="element-161">
                      <StudentDataCheck data-id="element-162" />
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-159" />
              <Route path="/teacher-messages" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-164">
                    <SubscriptionGuard data-id="element-165">
                      <TeacherLayout data-id="element-166">
                        <TeacherMessages data-id="element-167" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-163" />

              {/* Public routes - NO AUTH GUARD */}
              <Route path="/courses" element={<PublicCoursesPage data-id="element-169" />} data-id="element-168" />
              <Route path="/teacher/:id" element={<TeacherPublicProfilePage data-id="element-171" />} data-id="element-170" />
              <Route path="/public-profile/:teacherId" element={<TeacherPublicProfilePage data-id="element-173" />} data-id="element-172" />

              {/* Virtual classroom and grades - context-dependent protection */}
              <Route path="/student/lesson/:lessonId/join" element={<StudentDevGuard data-id="element-175">
                    <StudentAuthGuard data-id="element-176">
                      <VirtualClassroom data-id="element-177" />
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-174" />
              {/* Teacher Sheet Music page - PROTECTED */}
              <Route path="/teacher-sheet-music" element={<AuthGuard requiredUserType="teacher" redirectTo="/teacher-login" data-id="element-179">
                    <SubscriptionGuard data-id="element-180">
                      <TeacherLayout data-id="element-181">
                        <SheetMusicPage data-id="element-182" />
                      </TeacherLayout>
                    </SubscriptionGuard>
                  </AuthGuard>} data-id="element-178" />

              {/* Sheet music viewer pages - NO AUTH GUARD (public access) */}
              <Route path="/sheet-music/moonlight-sonata" element={<MoonlightSonataViewer data-id="element-184" />} data-id="element-183" />
              <Route path="/sheet-music/prelude-in-c-major" element={<PreludeInCMajorViewer data-id="element-186" />} data-id="element-185" />
              <Route path="/sheet-music/toccatina" element={<ToccatinaViewer data-id="element-188" />} data-id="element-187" />
              <Route path="/sheet-music/rondo-in-e-flat" element={<RondoInEFlatViewer data-id="element-190" />} data-id="element-189" />
              <Route path="/sheet-music/polonaise-in-f" element={<PolonaiseInFViewer data-id="element-192" />} data-id="element-191" />
              <Route path="/sheet-music/english-suite-1-bourree-1" element={<EnglishSuite1Bourree1Viewer data-id="element-194" />} data-id="element-193" />
              <Route path="/sheet-music/english-suite-1-bourree-2" element={<EnglishSuite1Bourree2Viewer data-id="element-196" />} data-id="element-195" />
              <Route path="/sheet-music/english-suite-2-bourree-1" element={<EnglishSuite2Bourree1Viewer data-id="element-198" />} data-id="element-197" />
              <Route path="/sheet-music/english-suite-2-bourree-2" element={<EnglishSuite2Bourree2Viewer data-id="element-200" />} data-id="element-199" />
              <Route path="/sheet-music/wtk1-prelude-c-major" element={<WTK1Prelude1Viewer data-id="element-202" />} data-id="element-201" />
              <Route path="/sheet-music/wtk1-fugue-c-major" element={<WTK1Fugue1Viewer data-id="element-204" />} data-id="element-203" />
              <Route path="/sheet-music/wtk1-prelude-c-minor" element={<WTK1Prelude2Viewer data-id="element-206" />} data-id="element-205" />
              <Route path="/sheet-music/aus-meines-herzens-grunde" element={<AusMeinesHerzensGrundeViewer data-id="element-208" />} data-id="element-207" />
              <Route path="/sheet-music/ich-dank-dir-lieber-herre" element={<IchDankDirLieberHerreViewer data-id="element-210" />} data-id="element-209" />
              <Route path="/sheet-music/ermuntere-dich-mein-schwacher-geist" element={<ErmuntereDichMeinSchwacherGeistViewer data-id="element-212" />} data-id="element-211" />
              <Route path="/sheet-music/gott-wie-gross-ist-deine-gute" element={<GottWieGrossIstDeineGuteViewer data-id="element-214" />} data-id="element-213" />
              <Route path="/sheet-music/gib-dich-zufrieden-und-sei-stille" element={<GibDichZufriedenUndSeiStilleViewer data-id="element-216" />} data-id="element-215" />
              <Route path="/sheet-music/gib-dich-zufrieden-und-sei-stille-d-minor" element={<GibDichZufriedenUndSeiStilleDMinorViewer data-id="element-218" />} data-id="element-217" />
              <Route path="/sheet-music/aria-bwv-515" element={<AriaBWV515Viewer data-id="element-220" />} data-id="element-219" />
              <Route path="/sheet-music/warum-betrubst-du-dich" element={<WarumBetrubstDuDichViewer data-id="element-222" />} data-id="element-221" />
              <Route path="/sheet-music/invention-no-2" element={<InventionNo2Viewer data-id="element-224" />} data-id="element-223" />
              <Route path="/sheet-music/invention-no-3" element={<InventionNo3Viewer data-id="element-226" />} data-id="element-225" />
              <Route path="/sheet-music/invention-no-4" element={<InventionNo4Viewer data-id="element-228" />} data-id="element-227" />
              <Route path="/sheet-music/invention-no-5" element={<InventionNo5Viewer data-id="element-230" />} data-id="element-229" />
              <Route path="/sheet-music/invention-no-7" element={<InventionNo7Viewer data-id="element-232" />} data-id="element-231" />
              <Route path="/sheet-music/invention-no-8" element={<InventionNo8Viewer data-id="element-234" />} data-id="element-233" />
              <Route path="/sheet-music/invention-no-10" element={<InventionNo10Viewer data-id="element-236" />} data-id="element-235" />
              <Route path="/sheet-music/invention-no-11" element={<InventionNo11Viewer data-id="element-238" />} data-id="element-237" />
              <Route path="/sheet-music/invention-no-13" element={<InventionNo13Viewer data-id="element-240" />} data-id="element-239" />
              <Route path="/sheet-music/invention-no-14" element={<InventionNo14Viewer data-id="element-242" />} data-id="element-241" />
              <Route path="/sheet-music/invention-no-15" element={<InventionNo15Viewer data-id="element-244" />} data-id="element-243" />
              <Route path="/sheet-music/view/:id" element={<GenericSheetMusicViewer data-id="element-246" />} data-id="element-245" />

              {/* Student registration flow - GATED by StudentDevGuard + StudentAuthGuard */}
              <Route path="/instrument-selection" element={<StudentDevGuard data-id="element-248">
                    <StudentAuthGuard data-id="element-249">
                      <InstrumentSelectionPage data-id="element-250" />
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-247" />
              <Route path="/teacher-matching" element={<StudentDevGuard data-id="element-252">
                    <StudentAuthGuard data-id="element-253">
                      <TeacherMatchingPage data-id="element-254" />
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-251" />

              {/* Payment related routes - GATED by StudentDevGuard + StudentAuthGuard */}
              <Route path="/payment-methods" element={<StudentDevGuard data-id="element-256">
                    <StudentAuthGuard data-id="element-257">
                      <StudentLayout data-id="element-258">
                        <PaymentMethodsPage data-id="element-259" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-255" />
              <Route path="/transaction-history" element={<StudentDevGuard data-id="element-261">
                    <StudentAuthGuard data-id="element-262">
                      <StudentLayout data-id="element-263">
                        <TransactionHistoryPage data-id="element-264" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-260" />
              <Route path="/subscription" element={<StudentDevGuard data-id="element-266">
                    <StudentAuthGuard data-id="element-267">
                      <StudentLayout data-id="element-268">
                        <SubscriptionManagementPage data-id="element-269" />
                      </StudentLayout>
                    </StudentAuthGuard>
                  </StudentDevGuard>} data-id="element-265" />

              {/* Account Management Routes - Role-aware, will apply teacher verification through RoleAwareLayout */}
              <Route path="/my-info" element={<RoleAwareLayout data-id="element-271">
                    <MyInfoPage data-id="element-272" />
                  </RoleAwareLayout>} data-id="element-270" />
              <Route path="/settings" element={<RoleAwareLayout data-id="element-274">
                    <SettingsPage data-id="element-275" />
                  </RoleAwareLayout>} data-id="element-273" />
              <Route path="/account-security" element={<RoleAwareLayout data-id="element-277">
                    <AccountSecurityPage data-id="element-278" />
                  </RoleAwareLayout>} data-id="element-276" />
              <Route path="/billing-payouts" element={<RoleAwareLayout data-id="element-280">
                    <BillingPayoutsPage data-id="element-281" />
                  </RoleAwareLayout>} data-id="element-279" />
              <Route path="/help-support" element={<RoleAwareLayout data-id="element-283">
                    <HelpSupportPage data-id="element-284" />
                  </RoleAwareLayout>} data-id="element-282" />

              {/* Public info pages - NO AUTH GUARD */}
              <Route path="/about" element={<AboutUsPage data-id="element-286" />} data-id="element-285" />
              <Route path="/knowledge-base/getting-started" element={<RoleAwareLayout data-id="element-288">
                    <KnowledgeBaseGettingStarted data-id="element-289" />
                  </RoleAwareLayout>} data-id="element-287" />
              <Route path="/knowledge-base/scheduling-lessons" element={<RoleAwareLayout data-id="element-291">
                    <KnowledgeBaseSchedulingLessons data-id="element-292" />
                  </RoleAwareLayout>} data-id="element-290" />
              <Route path="/knowledge-base/virtual-classroom" element={<RoleAwareLayout data-id="element-294">
                    <KnowledgeBaseVirtualClassroom data-id="element-295" />
                  </RoleAwareLayout>} data-id="element-293" />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-297" />} data-id="element-296" />
              <Route path="/cookie-policy" element={<CookiePolicyPage data-id="element-299" />} data-id="element-298" />
              <Route path="/terms-of-service" element={<TermsOfServicePage data-id="element-301" />} data-id="element-300" />
              <Route path="/refund-policy" element={<RefundPolicyPage data-id="element-303" />} data-id="element-302" />
              <Route path="/blog" element={<BlogListPage data-id="element-305" />} data-id="element-304" />
              <Route path="/blog/:slug" element={<BlogArticlePage data-id="element-307" />} data-id="element-306" />
            </Routes>
            {/* Single instance of SignInModal */}
            <SignInModal isOpen={isSignInModalOpen} onClose={closeSignInModal} data-id="element-308" />
            {/* Single instance of TeacherLoginModal */}
            <TeacherLoginModal isOpen={isTeacherLoginModalOpen} onClose={closeTeacherLoginModal} data-id="element-309" />
            {/* Add ToastContainer */}
            <ToastContainer position="top-right" data-id="element-310" />
            {/* Add Toaster for react-hot-toast */}
            <Toaster position="top-center" toastOptions={{
            duration: 5000,
            style: {
              background: theme === 'dark' ? '#1A2A52' : '#ffffff',
              color: theme === 'dark' ? '#ffffff' : '#1f2937',
              minWidth: '300px',
              padding: '16px',
              fontSize: '14px'
            },
            success: {
              iconTheme: {
                primary: '#4ade80',
                secondary: 'white'
              },
              style: {
                background: theme === 'dark' ? '#1A2A52' : '#ffffff',
                border: theme === 'dark' ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid rgba(74, 222, 128, 0.2)'
              }
            }
          }} containerStyle={{
            top: 80,
            zIndex: 9999
          }} data-id="element-311" />
          </BrowserRouter>
          <style jsx global data-id="element-312">{`
            @media (min-width: 1024px) {
              html {
                scroll-behavior: smooth;
              }
            }
            /* Global CSS variables for dropdown styling */
            :root {
              --navy-700: #0a1128;
              --navy-800: #1a2a52;
              --navy-900: #0f1626;
              --deep-pink: #e63946;
              --dropdown-shadow:
                0 10px 15px -3px rgba(0, 0, 0, 0.1),
                0 4px 6px -2px rgba(0, 0, 0, 0.05);
            }
            /* Global select dropdown styling - use CSS variables for theming */
            select option {
              background-color: var(--dropdown-bg, #1f2937);
              color: var(--dropdown-text, white);
            }
            select option:checked {
              background-color: var(--dropdown-bg-selected, #394b6c);
              color: var(--dropdown-text-selected, white);
            }
            select option:hover {
              background-color: var(--dropdown-bg-hover, #2e3e5c);
            }
            select {
              background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
              background-position: right 0.5rem center;
              background-repeat: no-repeat;
              background-size: 1.5em 1.5em;
              padding-right: 2.5rem;
              -webkit-appearance: none;
              -moz-appearance: none;
              appearance: none;
              border-radius: 0.5rem;
            }
            select:focus {
              z-index: 10;
            }
            select::-ms-expand {
              display: none;
            }
            select option:checked,
            select option:focus,
            select option:active,
            select option:hover:checked {
              background-color: var(--dropdown-bg-selected, #394b6c) !important;
              color: var(--dropdown-text-selected, white) !important;
              box-shadow: 0 0 10px 100px var(--dropdown-bg-selected, #394b6c)
                inset !important;
              -webkit-text-fill-color: var(
                --dropdown-text-selected,
                white
              ) !important;
            }
            /* Dark mode styles for date and time inputs */
            input[type='date'],
            input[type='time'] {
              color-scheme: auto;
            }
            .bg-navy-900 input[type='date'],
            .bg-navy-900 input[type='time'],
            .bg-navy-800 input[type='date'],
            .bg-navy-800 input[type='time'] {
              color-scheme: dark;
            }
            /* Specific dark mode overrides for navy theme */
            .bg-navy-900 {
              background-color: #0a1128 !important;
            }
            .bg-navy-800 {
              background-color: #1a2a52 !important;
            }
            /* Calendar and time picker dropdown styling */
            .bg-navy-900::-webkit-calendar-picker-indicator,
            .bg-navy-800::-webkit-calendar-picker-indicator {
              filter: invert(1);
              background-color: transparent;
            }
            /* Time dropdown styling for dark mode */
            input[type='time']::-webkit-calendar-picker-indicator {
              background-color: transparent;
            }
            /* Dark mode time dropdown background */
            input[type='time']::-webkit-time-picker,
            input[type='time']::-webkit-datetime-edit,
            input[type='time']::-webkit-inner-spin-button,
            input[type='time']::-webkit-clear-button {
              color: white;
            }
            /* Style the time dropdown background in dark mode */
            .bg-navy-900 input[type='time']::-webkit-list-button,
            .bg-navy-800 input[type='time']::-webkit-list-button,
            .bg-navy-900 input[type='time']::-webkit-time-picker,
            .bg-navy-800 input[type='time']::-webkit-time-picker,
            .bg-navy-900
              input[type='time']::-webkit-datetime-edit-fields-wrapper,
            .bg-navy-800
              input[type='time']::-webkit-datetime-edit-fields-wrapper {
              background-color: rgba(
                26,
                42,
                82,
                0.5
              ); /* #1A2A52 with 50% opacity */
              color: white;
            }
            /* Weekend cells style for time dropdown */
            .bg-navy-900 input[type='time'].weekend::-webkit-time-picker,
            .bg-navy-800 input[type='time'].weekend::-webkit-time-picker {
              background-color: rgba(
                26,
                42,
                82,
                0.3
              ); /* #1A2A52 with 30% opacity */
            }
            /* Override browser default styles for the time dropdown in dark mode */
            @media (prefers-color-scheme: dark) {
              input[type='time']::-webkit-calendar-picker-dropdown {
                background-color: rgba(
                  26,
                  42,
                  82,
                  0.5
                ) !important; /* #1A2A52 with 50% opacity */
              }
            }
            /* Firefox specific styles for dark mode time picker */
            @-moz-document url-prefix() {
              .bg-navy-900 input[type='time'],
              .bg-navy-800 input[type='time'] {
                background-color: rgba(
                  26,
                  42,
                  82,
                  0.5
                ); /* #1A2A52 with 50% opacity */
                color: white;
              }
            }
            /* Light background override */
            .bg-white select option {
              background-color: white;
              color: #1f2937;
            }
            .bg-white select option:checked {
              background-color: #394b6c;
              color: white;
            }
            .bg-white select option:hover {
              background-color: #f3f4f6;
              color: #1f2937;
            }
            .bg-white select option:checked,
            .bg-white select option:focus,
            .bg-white select option:active,
            .bg-white select option:hover:checked {
              background-color: #394b6c !important;
              color: white !important;
              box-shadow: 0 0 10px 100px #394b6c inset !important;
            }
            /* Remove the hardcoded student dropdown styles that were overriding our theme variables */
            /* The styles below will be replaced by the component-level CSS in FindTeachers.tsx */
          `}</style>
        </div>
      </SignInModalContext.Provider>
    </AuthProvider>;
}
export function App() {
  return <ThemeProvider data-id="element-313">
      <AppContent data-id="element-314" />
    </ThemeProvider>;
}